/**
 * Deferred reporting of OFFLINE MFA failures — CU-86akbg4qm.
 *
 * ── WHY NOT JUST CAPTURE ──────────────────────────────────────────────────
 *
 * The portal's Sentry transport has NO offline queue (`App.tsx` sets no
 * `transport`, so `@sentry/react` uses plain `fetch`). An event captured while
 * offline is created, its upload fails, and it is DISCARDED — measured on
 * staging 2026-09-24 on both the send and resolve legs. Capturing an offline
 * failure therefore records nothing, however it is tagged.
 *
 * ⚠️ MOBILE'S BREADCRUMB REASONING DOES NOT PORT. Mobile breadcrumbs offline
 * failures to avoid a reconnect BURST from the React Native SDK's envelope
 * cache. The web has no cache, so no burst — and a breadcrumb alone survives
 * only if a later event fires in the same page, which a successful retry does
 * not produce. On the web a breadcrumb alone is invisible.
 *
 * ── WHAT THIS DOES ────────────────────────────────────────────────────────
 *
 * Folds every offline failure into ONE in-memory record, then sends ONE event
 * once the network is demonstrably back — on whichever comes first:
 *
 *   • the next SUCCESSFUL MFA step (`flushOfflineFailures('success')`), which
 *     PROVES a request just reached the server — the stronger trigger
 *   • the window `online` event, after a settle delay (below)
 *
 * ⚠️ THE `online` FLUSH WAITS — adversarial review, 2026-09-28 (hypothesis,
 * not measured). `online` fires when an interface comes up, which on a real
 * Wi-Fi or cellular rejoin can precede usable DNS or routing. The record is
 * cleared when the event is CAPTURED, not when it is DELIVERED, so an
 * immediate flush into a not-yet-working network loses it for good. So the
 * `online` path waits `ONLINE_SETTLE_MS`, re-checks `navigator.onLine`, and
 * yields to a success flush if one arrives first. ⚠️ DevTools' Offline toggle
 * restores a working network instantly, so staging cannot exercise this race.
 *
 * One aggregated event per recovery, so no burst on reconnect.
 *
 * ⚠️ ACCEPTED LOSSES, both no worse than before this existed: a tab closed
 * while still offline; and an `online` event that fires on a captive portal
 * where the upload still fails.
 *
 * ── PHI (M3) ──────────────────────────────────────────────────────────────
 *
 * The record holds a count, step names, scrub-safe codes and timings — CLOSED
 * sets only. No identifier of any kind, no prose, no credential. Held to the
 * `CAPTURE_SITES` ceiling in `scrubSafeCallSites.test.ts`.
 */

import * as Sentry from '@sentry/react';
import { scrubSafeAuthCode } from './sentryAuthCode';
import { dedupeSafeMessage } from './sentryDedupe';

export interface OfflineFailure {
  /** Sentry `feature` tag of the reporting surface, e.g. `mfa-sms`. */
  feature: string;
  /** The step that failed, matching the step tag its captures use. */
  step: string;
  /** The raw SDK code, or '' — scrubbed here, never by the caller. */
  code: string;
  /**
   * `false` for a failure the predicate did NOT classify as offline but that
   * happened while the browser reported no network (`navigator.onLine`). A
   * telemetry hint only — it never changes what the patient is shown. It
   * exists to catch an offline failure carrying a code we did not predict,
   * which mobile measured on device (CU-86ajwh66w).
   */
  classified: boolean;
}

interface Pending {
  count: number;
  unclassified: number;
  features: Set<string>;
  steps: Set<string>;
  codes: Set<string>;
  firstAt: number;
}

let pending: Pending | null = null;
let listening = false;
let settleTimer: ReturnType<typeof setTimeout> | null = null;

/** How long after `online` before the network is trusted to carry the upload. */
export const ONLINE_SETTLE_MS = 10_000;

function cancelSettle(): void {
  if (settleTimer !== null) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
}

/** Tags cap at 200 characters in Sentry. Closed-set values, joined. */
function tagList(values: Set<string>): string {
  return [...values].sort().join(',').slice(0, 200);
}

function countBucket(n: number): string {
  if (n <= 1) return '1';
  if (n <= 3) return '2-3';
  return '4+';
}

function onOnline(): void {
  if (settleTimer !== null) return;
  settleTimer = setTimeout(() => {
    settleTimer = null;
    // Dropped again inside the window: keep the record and the listener, and
    // wait for the next `online`.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    flushOfflineFailures('online');
  }, ONLINE_SETTLE_MS);
}

function listen(): void {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('online', onOnline);
  listening = true;
}

function unlisten(): void {
  if (!listening || typeof window === 'undefined') return;
  window.removeEventListener('online', onOnline);
  listening = false;
}

/**
 * Record an offline failure. Breadcrumbs it (so any event that DOES go out in
 * this page carries it) and holds it for the recovery event.
 */
export function noteOfflineFailure(failure: OfflineFailure): void {
  // ⚠️ Named `safeCode` and passed as an explicit key-value pair below. The
  // shorthand form is the exact raw shape `scrubSafeCallSites.test.ts` bans, and
  // that scan cannot see that a shorthand already holds a scrubbed value. It
  // caught this, correctly.
  const safeCode = scrubSafeAuthCode(failure.code);
  Sentry.addBreadcrumb({
    category: failure.feature,
    level: 'warning',
    message: failure.classified
      ? `could not reach the server (${failure.step})`
      : `failed while the browser reported no network (${failure.step})`,
    data: { code: safeCode },
  });

  if (!pending) {
    pending = {
      count: 0,
      unclassified: 0,
      features: new Set(),
      steps: new Set(),
      codes: new Set(),
      firstAt: Date.now(),
    };
  }
  pending.count += 1;
  if (!failure.classified) pending.unclassified += 1;
  pending.features.add(failure.feature);
  pending.steps.add(failure.step);
  pending.codes.add(safeCode);
  listen();
}

/**
 * Send the held record as ONE event, if there is one. Safe to call on every
 * success — it does nothing when nothing is pending.
 */
export function flushOfflineFailures(trigger: 'online' | 'success'): void {
  cancelSettle();
  unlisten();
  const held = pending;
  pending = null;
  if (!held) return;

  Sentry.captureMessage(dedupeSafeMessage('mfa: offline failures recovered'), {
    level: 'warning',
    // ⚠️ STABLE — decides the issue. The message varies (see `sentryDedupe`);
    // this must not, or every recovery would open a new issue.
    fingerprint: ['mfa', 'offline-failures-recovered'],
    tags: {
      feature: tagList(held.features),
      platform_client: 'portal',
      offline_steps: tagList(held.steps),
      // ⚠️ A TAG, scrub-safe (CU-86akbcqv8) — `extra` would arrive `[Filtered]`.
      auth_error_code: tagList(held.codes),
      offline_count: countBucket(held.count),
      flush_trigger: trigger,
    },
    extra: {
      failureCount: held.count,
      unclassifiedCount: held.unclassified,
      msSinceFirstFailure: Date.now() - held.firstAt,
    },
  });
}

/** Exported for tests — module state would otherwise leak between them. */
export function __resetOfflineReportForTest(): void {
  cancelSettle();
  unlisten();
  pending = null;
}
